import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, NavLink, useSearchParams } from "react-router-dom";
import { useRegister, useGetOTP, useVerifyEmail } from "@/hooks/use-mutations";
import Throbber from "@/components/throbber";
import Logo from "@/components/logo";
import { useState } from "react";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { handleError } from "@/lib/helper";
import { toast } from "sonner";
import { useModalStore } from "@/zustand/modalStore";
import Modal from "@/components/modals";

const schema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z
      .string()
      .trim()
      .min(1, { message: "Email is required" })
      .email("Invalid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[\W_]/, "Password must contain at least one special character"),
    confirmPassword: z.string().min(6, "Please confirm your password"),
    refBy: z.string().min(1, "Referral code is required"),
    otp: z.string().min(6, "Authenticator code must be 6 digits"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

type FormData = z.infer<typeof schema>;

const Signup = () => {
  const [searchParams] = useSearchParams();
  const refBy = searchParams?.get("ref") || "";
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const { openModal } = useModalStore();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      otp: "",
      refBy: refBy ?? "",
    },
  });
  const { mutateAsync: getOtp, isPending: isGettingOtp } = useGetOTP();
  const { mutateAsync: register, isPending: isRegistering } = useRegister();
  const { mutateAsync: verify, isPending: isVerifying } = useVerifyEmail();

  const handleGetOtp = async (
    partialData: Pick<FormData, "email" | "refBy">,
  ) => {
    try {
      const isValid = await form.trigger([
        "email",
        "firstName",
        "lastName",
        "password",
        "confirmPassword",
        "refBy",
      ]);

      if (!isValid) return;
      const checkResponse = await verify({
        email: partialData.email,
        refBy: partialData.refBy,
      });
      if (checkResponse.data.emailExists) {
        form.setError("email", {
          type: "required",
          message: "Email already exists",
        });
        return;
      }
      if (
        "refByExists" in checkResponse.data &&
        !checkResponse.data.refByExists
      ) {
        form.setError("refBy", {
          type: "required",
          message: "Invalid referral code",
        });
        return;
      }
      const response = await getOtp({
        emailAddress: partialData.email,
        purpose: "register",
      });
      if (response.status === 200) setStep(2);
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      await register(data);
      toast.success("Registration successful, please login");
      openModal("success", { type: "register" });
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="text-auth-text">
      <div className="relative flex min-h-dvh">
        {/* Left panel. `aspect-[617/832]` matches the artwork's viewBox so the
            column is the correct width before the image decodes, and
            `bg-auth-panel` fills it in the meantime. */}
        <div className="bg-auth-panel fixed inset-y-0 left-0 hidden aspect-617/832 h-screen lg:block">
          <img
            src="/images/auth-image2.svg"
            className="h-full w-auto"
            alt="The New Standard of Digital Finance. Easily create virtual cards to handle every online purchase, bill, and subscription."
          />
        </div>
        {/* The layer above is `fixed` and out of flow, so this reserves the
            column width. Same ratio, no second image download. */}
        <div className="hidden aspect-617/832 h-screen lg:block" />
        <div className="bg-auth-surface relative flex grow flex-col items-center justify-center py-10">
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex w-[320px] flex-col items-start md:w-[400px] lg:w-full lg:max-w-sm"
          >
            <Logo className="text-auth-action w-32" />
            <div className="bg-auth-track border-auth-stroke mt-4 inline-flex gap-2 rounded border p-0.5">
              <NavLink
                to="/login?o=0"
                className={({ isActive }) =>
                  `inline-flex w-24 items-center justify-center rounded px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-auth-action text-auth-on-action" : "text-auth-muted hover:text-auth-text"}`
                }
              >
                Login
              </NavLink>
              <NavLink
                to="/signup"
                className={({ isActive }) =>
                  `inline-flex w-24 items-center justify-center rounded px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-auth-action text-auth-on-action" : "text-auth-muted hover:text-auth-text"}`
                }
              >
                Sign Up
              </NavLink>
            </div>
            <Form {...form}>
              <div className="mt-6 flex w-full flex-col gap-4 lg:min-w-sm">
                {/* Step 1 */}
                {step === 1 && (
                  <>
                    <div>
                      <h1 className="text-2xl font-semibold">
                        Create an account
                      </h1>
                      <p>Sign up to get started with your account</p>
                    </div>
                    <div className="mt-2 flex gap-2.5 *:grow">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-1">
                            <FormLabel className="text-xs">
                              First name
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                                {...field}
                                placeholder="Enter first name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-1">
                            <FormLabel className="text-xs">
                              Last name
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                                {...field}
                                placeholder="Enter last name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <FormLabel className="text-xs">
                            Email address
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                              {...field}
                              onChange={(e) => {
                                const cleaned = e.target.value
                                  .replace(/\s+/g, "")
                                  .toLowerCase();
                                field.onChange(cleaned);
                                form.clearErrors("email");
                              }}
                              placeholder="Enter your email address"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <FormLabel className="text-xs">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                                type={showPassword ? "text" : "password"}
                                {...field}
                                placeholder="Enter your password"
                              />
                              <Button
                                type="button"
                                variant={"ghost"}
                                className="text-auth-muted absolute right-0"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <Eye /> : <EyeOff />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <FormLabel className="text-xs">
                            Confirm password
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                                type={showConfirmPassword ? "text" : "password"}
                                {...field}
                                placeholder="Confirm your password"
                              />
                              <Button
                                type="button"
                                variant={"ghost"}
                                className="text-auth-muted absolute right-0"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                              >
                                {showConfirmPassword ? <Eye /> : <EyeOff />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="refBy"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <FormLabel className="text-xs">
                            Referral code
                          </FormLabel>
                          <FormControl>
                            <Input
                              disabled={refBy !== ""}
                              className="border-auth-stroke placeholder:text-auth-muted rounded placeholder:text-[10px]"
                              placeholder="Enter your referral code"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                form.clearErrors("refBy");
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      disabled={isVerifying || isGettingOtp}
                      className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-6 h-11 w-full rounded"
                      type="button"
                      onClick={() => handleGetOtp(form.getValues())}
                    >
                      {isGettingOtp || isVerifying ? <Throbber /> : "Continue"}
                    </Button>
                    <p className="text-auth-text mt-4 text-center text-xs font-medium">
                      Already have an account?{" "}
                      <Link
                        className="text-auth-link hover:text-auth-link/80"
                        to="/login"
                      >
                        Sign In
                      </Link>
                    </p>
                  </>
                )}

                {/* Step 2 */}
                {step === 2 && (
                  <>
                    <Button
                      className="absolute -top-32 -left-2 size-8"
                      variant={"ghost"}
                      type="button"
                      onClick={() => setStep(1)}
                    >
                      <ArrowLeft />
                    </Button>
                    <div>
                      <h1 className="text-2xl font-semibold">
                        Let&apos;s verify your Email
                      </h1>
                      <p className="text-auth-muted text-sm leading-3.5">
                        We’ve sent a 6-digit code to your email, enter the code
                        below to verify
                      </p>
                    </div>

                    <div className="mt-2 flex w-full flex-col gap-2.5">
                      <FormField
                        control={form.control}
                        name="otp"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-1">
                            <FormLabel className="mb-1 text-xs font-normal">
                              Enter verification code
                            </FormLabel>
                            <FormControl>
                              <InputOTP
                                autoFocus
                                autoComplete="off"
                                pattern={REGEXP_ONLY_DIGITS}
                                maxLength={6}
                                {...field}
                              >
                                <InputOTPGroup className="*:border-auth-otp-stroke gap-3 *:!rounded *:border *:shadow-none">
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        disabled={isRegistering}
                        className="bg-auth-action text-auth-on-action hover:bg-auth-action/90 mt-4 h-11 w-full rounded"
                        type="submit"
                      >
                        {isRegistering ? <Throbber /> : "Verify"}
                      </Button>
                    </div>
                  </>
                )}

              </div>
            </Form>
          </form>
        </div>
      </div>
      <Modal />
    </div>
  );
};

export default Signup;
